// src/components/CourseCompletionHelper.jsx
// 🎓 TICKET JPRXH6 - Helper para marcar actividades como completadas al regresar
// 🎓 TICKET GHTVN9 - Registro de evento 'complete' en analítica
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, X } from 'lucide-react';
import { markActivityComplete } from '../courses/ProgressManager';
import { recordActivityComplete } from '../courses/courseAnalyticsApi'; // 🎓 TICKET GHTVN9

/**
 * Componente que detecta cuando el usuario regresa de una experiencia VRT/EPL
 * y muestra un prompt para marcar la actividad como completada.
 * 
 * Se activa cuando detecta parámetros específicos en localStorage que indican
 * que el usuario viene de completar una actividad de curso.
 */
const CourseCompletionHelper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pendingCompletion, setPendingCompletion] = useState(null);

  useEffect(() => {
    // Verificar si hay una actividad pendiente de marcar como completada
    const checkPendingCompletion = () => {
      try {
        const pendingStr = localStorage.getItem('edutech_pending_activity_completion');
        if (pendingStr) {
          const pending = JSON.parse(pendingStr);
          
          // Verificar que estamos en una ruta que no sea la del curso
          // (es decir, que el usuario salió del curso para hacer la actividad)
          if (!location.pathname.startsWith('/courses/')) {
            setPendingCompletion(pending);
          }
        }
      } catch (error) {
        console.error('Error leyendo pending completion:', error);
      }
    };

    checkPendingCompletion();
  }, [location]);

  const handleMarkComplete = async () => {
    if (!pendingCompletion) return;

    const { courseId, activityId, activityTitle, activityType } = pendingCompletion;

    // 🎓 TICKET GHTVN9 - Registrar evento 'complete' (durationSeconds = 0 para VRT/EPL por ahora)
    if (activityType) {
      await recordActivityComplete(courseId, activityId, activityType, 0);
      console.log('📊 Analytics: Evento COMPLETE registrado desde CourseCompletionHelper');
    }

    // Marcar como completada
    await markActivityComplete(courseId, activityId);

    // Limpiar localStorage
    localStorage.removeItem('edutech_pending_activity_completion');

    // Navegar de vuelta al curso
    navigate(`/courses/${courseId}`);
    
    setPendingCompletion(null);
  };

  const handleDismiss = () => {
    // Limpiar sin marcar como completada
    localStorage.removeItem('edutech_pending_activity_completion');
    setPendingCompletion(null);
  };

  if (!pendingCompletion) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 max-w-sm">
      <div className="bg-white rounded-2xl shadow-2xl border-2 border-blue-500 p-4 animate-in slide-in-from-bottom-5">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <CheckCircle className="w-6 h-6 text-blue-600" />
          </div>
          
          <div className="flex-1">
            <h4 className="font-bold text-gray-900 mb-1">
              ¿Completaste esta actividad?
            </h4>
            <p className="text-sm text-gray-600 mb-3">
              {pendingCompletion.activityTitle}
            </p>
            
            <div className="flex gap-2">
              <button
                onClick={handleMarkComplete}
                className="flex-1 px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                Sí, completada
              </button>
              <button
                onClick={handleDismiss}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseCompletionHelper;
